import { EditorView } from 'prosemirror-view'
import React, { Component } from 'react'
import { createPortal } from 'react-dom'

import { markdownSerializer } from '../pm/markdown/serializer'
import { lineWidthStyle } from './line-breaks'

// Zero-width / invisible control chars that pasted web content drags along and
// that otherwise pollute the document (BOM, ZWSP/ZWNJ/ZWJ, soft hyphen, C0
// controls except tab/newline).
const INVISIBLE_CHARS =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u00AD\u200B-\u200D\u2060\uFEFF]/g

// A soft hyphen at a line end joins the word halves (PDFs); stripped alone it would leave a break
const stripInvisible = (value: string) =>
  value.replace(/­[ \t]*\r?\n[ \t]*/g, '').replace(INVISIBLE_CHARS, '')

// ProseMirror pastes raw against markdownSchema, where the info/reference nodes
// greedily match every <a> and require an href. Sources like projekt-gutenberg
// embed href-less <a name="pageNN"> anchors mid-text, so parsing throws
// "No value supplied for attribute href" and the whole paste is silently
// dropped. Unwrap those anchors (keeping their text) before ProseMirror parses.
const sanitizePastedHTML = (html: string) => {
  const doc = new DOMParser().parseFromString(stripInvisible(html), 'text/html')
  doc.querySelectorAll('a:not([href])').forEach((a) => {
    a.replaceWith(...Array.from(a.childNodes))
  })
  return doc.body.innerHTML
}

type OwnProps = {
  editorState?: any
  onEditorState: (...args: Array<any>) => any
  onChange: (value: any) => void
  onBlur?: (...args: Array<any>) => any
  handleAddInfo: (...args: Array<any>) => any
  handleAddReference: (...args: Array<any>) => any
  handleAddMedia: (...args: Array<any>) => any
  testId?: string
  readOnly?: boolean
  lineWidth?: number
}

type State = {
  editorView: EditorView | null
  editorState: any
}

type Props = OwnProps

export default class EditingView extends Component<Props, State> {
  static defaultProps = {
    onChange: () => {
      /* no-op */
    },
  }

  createEditorView: (instance: HTMLDivElement | null) => void
  editorView: EditorView | null = null
  tooltipRef: HTMLDivElement | null = null

  state: State = {
    editorView: null,
    editorState: null,
  }

  editorDOM: HTMLDivElement | null = null

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const { editorView, editorState } = prevState
    if (editorView && editorState) {
      // No need to update readonly as we'll get a
      // fresh new component due to keying on value
      if (nextProps.editorState !== editorState && !nextProps.readOnly) {
        editorView.updateState(nextProps.editorState)
        // Disabled for now, does weird focus stealing on click handlers
        // editorView.setProps({ editable: () => !nextProps.readOnly })
      }
      return null
    } else {
      return { editorState: nextProps.editorState }
    }
  }

  constructor(props: Props) {
    super(props)
    this.createEditorView = (element: HTMLDivElement | null) => {
      // The callback ref can fire multiple times (e.g. StrictMode
      // detaches and reattaches refs) — always tear down the previous
      // view so only one ProseMirror instance is mounted in the DOM
      if (this.editorView) {
        this.editorView.destroy()
        this.editorView = null
      }
      if (element == null) {
        this.editorDOM = null
      }
      if (element != null) {
        const editorView = new EditorView(element, {
          state: this.state.editorState,
          dispatchTransaction: this.dispatchTransaction,
          transformPastedHTML: sanitizePastedHTML,
          transformPastedText: stripInvisible,
          handleDOMEvents: {
            // closest(): a link's text can sit inside <em> or <strong>
            mouseover: (_view, event) => {
              const link = (event.target as HTMLElement)?.closest?.('a[href]')
              if (link) {
                this.showTooltip(link)
                return true
              }
              return false
            },
            mouseout: (_view, event) => {
              const link = (event.target as HTMLElement)?.closest?.('a')
              if (link && !link.contains(event.relatedTarget as Node | null)) {
                this.hideTooltip()
                return true
              }
              return false
            },
            click: (_view, event) => {
              const link = (event.target as HTMLElement)?.closest?.('a[href]')
              if (link) {
                event.preventDefault()
                let href = link.getAttribute('href')
                if (href) {
                  if (
                    !href.startsWith('http://') &&
                    !href.startsWith('https://')
                  ) {
                    href = 'https://' + href
                  }
                  window.open(href, '_blank')
                }
                return true
              }
              return false
            },
          },
        })
        editorView.dom.addEventListener('blur', () => {
          // return the markdown and plain text
          this.props.onBlur &&
            this.props.onBlur({
              markdown: markdownSerializer.serialize(
                this.props.editorState.doc
              ),
              text: this.props.editorState.doc.textContent,
            })
        })
        this.editorView = editorView
        this.setState({ editorView })
        this.editorDOM = element
      }
    }
  }

  // Floats over the page instead of taking room in the editor, so a long URL
  // can't widen the field it sits in; kept inside the viewport on both axes
  showTooltip = (target) => {
    const tooltip = this.tooltipRef
    if (!tooltip) {
      return
    }
    tooltip.textContent = target.getAttribute('href')
    const rect = target.getBoundingClientRect()
    const { width, height } = tooltip.getBoundingClientRect()
    const margin = 8
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - width / 2, margin),
      window.innerWidth - width - margin
    )
    const above = rect.top - height - 6
    tooltip.style.left = `${left}px`
    tooltip.style.top = `${above >= margin ? above : rect.bottom + 6}px`
    tooltip.style.opacity = '1'
    // Fixed, so it would stay behind while the link scrolls away
    window.addEventListener('scroll', this.hideTooltip, true)
  }

  hideTooltip = () => {
    window.removeEventListener('scroll', this.hideTooltip, true)
    if (this.tooltipRef) {
      this.tooltipRef.style.opacity = '0'
    }
  }

  shouldComponentUpdate() {
    return false
  }

  componentWillUnmount() {
    this.hideTooltip()
    this.editorView?.destroy()
    this.editorView = null
  }

  dispatchTransaction = (tx: any) => {
    if (this.props.readOnly) {
      return null
    }
    // Apply against the view's own state — props.editorState can lag
    // behind and would throw "Applying a mismatched transaction"
    const currentState = this.editorView?.state ?? this.props.editorState
    const editorState = currentState.apply(tx)
    // A deleted link never fires mouseout
    if (tx.docChanged) {
      this.hideTooltip()
    }
    if (this.editorView != null) {
      this.editorView.updateState(editorState)
    }
    this.props.onEditorState(editorState)
    setTimeout(() => {
      const attributes = tx.getMeta('attributes')
      if (!attributes) {
        return
      }

      // Transactions can categorise themselves with
      // additional metadata
      // tr.setMeta('attribute', {extraDeets: 'here'})
      switch (attributes.type) {
        case 'insertInfoBlock': {
          const { id, value, isRef } = attributes
          this.props.handleAddInfo(id, value, isRef)
          this.state.editorView?.focus()
          break
        }
        case 'insertReferenceBlock': {
          const { refId, blockId, page, additionalInfo } = attributes
          this.props.handleAddReference({
            refId,
            blockId,
            page,
            additionalInfo,
          })
          this.state.editorView?.focus()
          break
        }
        case 'insertMediaItem': {
          this.props.handleAddMedia()
          break
        }
      }
    }, 0)
    this.props.onChange({
      markdown: markdownSerializer.serialize(editorState.doc),
      text: editorState.doc.textContent,
    })
  }

  render() {
    return (
      <div className="pm-editor-container" style={lineWidthStyle(this.props.lineWidth)}>
        <div
          className="pm-editor"
          data-cy={this.props.testId}
          ref={this.createEditorView}
        />
        {createPortal(
          <div
            ref={(ref) => {
              this.tooltipRef = ref
            }}
            className="link-tooltip"
          />,
          document.body
        )}
      </div>
    )
  }
}
